/**
 * Collapsible renderer for namespaced MCP tools (e.g. `atlassian__getJiraIssue`).
 *
 * Server-side MCP tools are exposed through pi-coding-agent with the qualified
 * name `<serverId>__<toolName>` (see backend/src/mcp/cowork-tools.ts). Without
 * a custom renderer, pi-web-ui's ChatPanel dumps the entire JSON blob inline —
 * for Atlassian's `getJiraIssue` that's several KB of custom fields per call.
 *
 * pi-web-ui's renderer registry is exact-name keyed and pi-web-ui's exports
 * are locked down (no wildcard pattern, no Map access), so we register an
 * explicit renderer per tool name. Names are discovered by the backend on
 * connect and pushed to the frontend via `/api/cowork/mcp-status`; callers
 * pass them in via `registerMcpRenderers([...])`.
 */

import { registerToolRenderer, renderCollapsibleHeader } from "@earendil-works/pi-web-ui";
import type { ToolResultMessage } from "@earendil-works/pi-ai";
import type { ToolRenderer, ToolRenderResult } from "@earendil-works/pi-web-ui";
import { html } from "lit";
import { createRef, ref } from "lit/directives/ref.js";
import { Wrench } from "lucide";

function prettyMcpName(qualified: string): string {
	const idx = qualified.indexOf("__");
	if (idx < 0) return qualified;
	const server = qualified.slice(0, idx);
	const tool = qualified.slice(idx + 2);
	const serverLabel = server.charAt(0).toUpperCase() + server.slice(1);
	return `${serverLabel}: ${tool}`;
}

function extractText(result: ToolResultMessage | undefined): string {
	if (!result) return "";
	const content = (result as unknown as { content?: Array<{ type: string; text?: string }> }).content;
	if (!Array.isArray(content)) return "";
	return content
		.filter((c) => c.type === "text" && c.text)
		.map((c) => c.text ?? "")
		.join("\n");
}

function makeMcpRenderer(qualifiedName: string): ToolRenderer {
	return {
		render(params, result, isStreaming): ToolRenderResult {
			const isError = Boolean((result as unknown as { isError?: boolean })?.isError);
			const state = isStreaming ? "inprogress" : isError ? "error" : "complete";
			const contentRef = createRef<HTMLElement>();
			const chevronRef = createRef<HTMLElement>();
			const argsJson = params ? JSON.stringify(params, null, 2) : "";
			const resultText = extractText(result);
			return {
				isCustom: true,
				content: html`
					${renderCollapsibleHeader(state, Wrench, prettyMcpName(qualifiedName), contentRef, chevronRef, false)}
					<div
						class="overflow-hidden max-h-0 transition-all duration-200 space-y-2"
						${ref(contentRef)}
					>
						${argsJson
							? html`<div>
									<div class="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Args</div>
									<pre class="text-xs bg-muted/40 rounded p-2 overflow-x-auto">${argsJson}</pre>
								</div>`
							: ""}
						${resultText
							? html`<div>
									<div class="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Result</div>
									<pre class="text-xs bg-muted/40 rounded p-2 overflow-x-auto whitespace-pre-wrap wrap-break-word">${resultText}</pre>
								</div>`
							: ""}
					</div>
				`,
			};
		},
	};
}

const registered = new Set<string>();

/**
 * Register a collapsible renderer for each given MCP tool name. Idempotent —
 * subsequent calls for the same name are no-ops. Names should be the fully
 * qualified `<serverId>__<toolName>` form that the backend exposes.
 */
export function registerMcpRenderers(names: readonly string[]): void {
	for (const name of names) {
		if (registered.has(name)) continue;
		if (!name.includes("__")) continue;
		registerToolRenderer(name, makeMcpRenderer(name));
		registered.add(name);
	}
}
